/**
 * POS product helpers shared by register grid, editor, and stores.
 */

/** Page size when fetching all products in a category (matches useRegisterGridDrill). */
export const POS_CATEGORY_PRODUCTS_PAGE_SIZE = 50

/**
 * Resolve a product's canonical category id from API/search/store shapes.
 * @param {object|null|undefined} product
 * @returns {number|null}
 */
export function resolveProductCategoryId (product) {
  if (!product || typeof product !== 'object') return null
  const raw = product.canonical_category_id
    ?? product.category_id
    ?? product.canonical_category?.id
    ?? product.canonicalCategory?.id
    ?? product.category?.id
    ?? null
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : null
}

/**
 * Normalize an API product record for POS grid/editor usage.
 * @param {object|null|undefined} product
 * @returns {object|null}
 */
export function normalizePosProductRecord (product) {
  if (!product || typeof product !== 'object') return null
  const id = Number(product.id)
  if (!Number.isFinite(id) || id <= 0) return null

  const categoryId = resolveProductCategoryId(product)

  return {
    id,
    name: product.name,
    price: product.price,
    barcode: product.barcode || null,
    non_stock: !!product.non_stock,
    non_taxable: !!product.non_taxable,
    category_id: categoryId,
    canonical_category_id: categoryId,
    grid_image_url: product.grid_image_url || null,
    grid_image_thumb_url: product.grid_image_thumb_url || null,
    first_image_url: product.first_image_url || null,
    first_image_thumb_url: product.first_image_thumb_url || null,
    has_images: !!product.has_images,
    ...(typeof product.has_pos_modifiers === 'boolean' ? { has_pos_modifiers: !!product.has_pos_modifiers } : {}),
    allow_fractional: !!product.allow_fractional,
    ring_by_amount: !!product.ring_by_amount,
    always_enter_quantity: !!product.always_enter_quantity,
    open_price: !!product.open_price,
    price_decimals: product.price_decimals === 3 ? 3 : 2,
    include_in_grid: !!product.include_in_grid,
    archived: !!product.archived
  }
}

/**
 * Whether a product belongs to the given category id.
 * @param {object|null|undefined} product
 * @param {number|string} categoryId
 */
export function productInCategory (product, categoryId) {
  const catId = Number(categoryId)
  if (!Number.isFinite(catId) || catId <= 0) return false
  return resolveProductCategoryId(product) === catId
}
